import React, { useState } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, Users, Building2, FileText, Receipt, Wallet, Loader2, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const DATA_TYPES = [
  { value: 'customers', label: 'Customers', icon: Users, description: 'Import customer records with payment terms' },
  { value: 'suppliers', label: 'Suppliers', icon: Building2, description: 'Import supplier records with payment terms' },
  { value: 'invoices', label: 'Invoices (AR)', icon: FileText, description: 'Import accounts receivable invoices' },
  { value: 'bills', label: 'Bills (AP)', icon: Receipt, description: 'Import accounts payable bills' },
  { value: 'other_cash', label: 'Other Cash', icon: Wallet, description: 'Import other cash movements (wages, rent, etc.)' }
];

export default function ImportPage() {
  const [selectedType, setSelectedType] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleDownloadTemplate = async () => {
    if (!selectedType) {
      toast.error('Please select a data type first');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/import/template/${selectedType}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedType}_template.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setResult(null);
    } else if (selectedFile) {
      toast.error('Please select a CSV file');
    }
  };

  const handleImport = async () => {
    if (!selectedType) {
      toast.error('Please select a data type');
      return;
    }
    if (!file) {
      toast.error('Please select a file to import');
      return;
    }

    setUploading(true);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = selectedType === 'other_cash' ? 'other-cash' : selectedType;
      const response = await axios.post(`${API}/import/${endpoint}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResult(response.data);
      
      if (response.data.success > 0 && response.data.failed === 0) {
        toast.success(`Successfully imported ${response.data.success} records`);
      } else if (response.data.success > 0) {
        toast.warning(`Imported ${response.data.success} records, ${response.data.failed} failed`);
      } else {
        toast.error(`Import failed: ${response.data.failed} errors`);
      }
    } catch (error) {
      toast.error('Import failed: ' + (error.response?.data?.detail || 'Unknown error'));
    } finally {
      setUploading(false);
    }
  };

  const selectedTypeInfo = DATA_TYPES.find(t => t.value === selectedType);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Import Data
          </h1>
          <p className="text-slate-500 mt-1">
            Bulk import customers, suppliers, invoices, bills, and other cash entries from CSV files
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Import Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-primary" />
                Upload CSV
              </CardTitle>
              <CardDescription>
                Select a data type, download the template, fill it out, and upload
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Step 1: Select Type */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">1. Select Data Type</Label>
                <Select value={selectedType} onValueChange={setSelectedType}>
                  <SelectTrigger data-testid="import-type-select">
                    <SelectValue placeholder="Choose what to import..." />
                  </SelectTrigger>
                  <SelectContent>
                    {DATA_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex items-center gap-2">
                          <type.icon className="w-4 h-4" />
                          {type.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedTypeInfo && (
                  <p className="text-xs text-slate-500">{selectedTypeInfo.description}</p>
                )}
              </div>

              {/* Step 2: Download Template */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">2. Download Template</Label>
                <Button
                  variant="outline"
                  onClick={handleDownloadTemplate}
                  disabled={!selectedType}
                  className="w-full gap-2"
                  data-testid="download-template-btn"
                >
                  <Download className="w-4 h-4" />
                  Download {selectedType ? `${selectedTypeInfo?.label} Template` : 'Template'}
                </Button>
              </div>

              {/* Step 3: Upload File */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">3. Upload Filled CSV</Label>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-primary transition-colors">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="hidden"
                    id="csv-upload"
                    data-testid="csv-file-input"
                  />
                  <label htmlFor="csv-upload" className="cursor-pointer">
                    <FileSpreadsheet className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                    {file ? (
                      <p className="text-sm text-slate-700 font-medium">{file.name}</p>
                    ) : (
                      <p className="text-sm text-slate-500">Click to select CSV file</p>
                    )}
                  </label>
                </div>
              </div>

              {/* Step 4: Import */}
              <Button
                onClick={handleImport}
                disabled={!selectedType || !file || uploading}
                className="w-full gap-2"
                data-testid="import-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Import Data
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Import Results */}
          <Card>
            <CardHeader>
              <CardTitle>Import Results</CardTitle>
              <CardDescription>
                View the results of your last import operation
              </CardDescription>
            </CardHeader>
            <CardContent>
              {result ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="text-sm font-medium text-green-700">Successful</span>
                      </div>
                      <p className="text-2xl font-bold text-green-600 mt-1">{result.success}</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="flex items-center gap-2">
                        <XCircle className="w-5 h-5 text-red-600" />
                        <span className="text-sm font-medium text-red-700">Failed</span>
                      </div>
                      <p className="text-2xl font-bold text-red-600 mt-1">{result.failed}</p>
                    </div>
                  </div>

                  {/* Errors */}
                  {result.errors && result.errors.length > 0 && (
                    <div className="bg-amber-50 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <span className="text-sm font-medium text-amber-700">Errors (showing first 10)</span>
                      </div>
                      <ul className="space-y-1">
                        {result.errors.map((error, idx) => (
                          <li key={idx} className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1">
                            {error}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">Import results will appear here</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Import Order Guide */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Import Order Guide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 items-center text-sm">
              <span className="bg-slate-100 px-3 py-1 rounded-full font-medium">1. Customers</span>
              <span className="text-slate-400">→</span>
              <span className="bg-slate-100 px-3 py-1 rounded-full font-medium">2. Suppliers</span>
              <span className="text-slate-400">→</span>
              <span className="bg-slate-100 px-3 py-1 rounded-full font-medium">3. Invoices</span>
              <span className="text-slate-400">→</span>
              <span className="bg-slate-100 px-3 py-1 rounded-full font-medium">4. Bills</span>
              <span className="text-slate-400">→</span>
              <span className="bg-slate-100 px-3 py-1 rounded-full font-medium">5. Other Cash</span>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Import customers and suppliers first, as invoices and bills reference them by name.
            </p>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
